import { defineStore } from 'pinia'
import { ref } from 'vue'

import { SfLog } from '@app/adapters/logger'
import { fetchColumnsRequest } from '@app/application/report/api/columns'

import { settleColumn } from './column-constraints'

import type {
  ColumnType,
  ColumnValues,
  EditorColumn,
  EditorStatus,
  TypeSchema,
} from './types'

// The column types the "+" buttons can add, in the order they appear.
export const ADDABLE_TYPES = [
  'ColumnSpacer',
  'Period',
  'PeriodDifference',
] as const

export type AddableType = typeof ADDABLE_TYPES[number]

let uidCounter = 0
const nextUid = (): string => {
  uidCounter += 1
  return `col-${uidCounter}`
}

// The working state of the inline column editor drawer. Lives in a store rather
// than in the Stimulus controller because both the controller (which opens the
// drawer and knows which report is on screen) and the Vue components need it.
export const useColumnEditor = defineStore('reportColumnEditor', () => {
  const isOpen = ref(false)
  const status = ref<EditorStatus>('idle')
  const columns = ref<EditorColumn[]>([])
  const schema = ref<Partial<Record<ColumnType, TypeSchema>>>({})
  const defaults = ref<Partial<Record<ColumnType, ColumnValues>>>({})

  const reportId = ref<number | null>(null)
  const reportPeriodSetId = ref<number | null>(null)

  // Which report — and which of its period sets — the drawer edits. Comes from
  // the subnav button's action params.
  //
  // Everything below belongs to these two ids, so pointing the drawer at a
  // different report (or period set) drops what was loaded for the old one.
  // Without that, load()'s already-loaded guard would serve the previous
  // report's columns.
  //
  // No page can currently make that happen: the ids are baked into the subnav
  // button at render time, and a Turbolinks visit tears the whole Pinia
  // instance down (see adapters/singleton/page). The reset is here because the
  // store's ids are settable and the guard downstream trusts them, not to
  // defend against a caller we have.
  function configure(ids: {
    reportId: number
    reportPeriodSetId: number | null
  }): void {
    if (
      reportId.value === ids.reportId &&
      reportPeriodSetId.value === ids.reportPeriodSetId
    ) {
      return
    }

    reportId.value = ids.reportId
    reportPeriodSetId.value = ids.reportPeriodSetId

    columns.value = []
    schema.value = {}
    defaults.value = {}
    status.value = 'idle'
  }

  // Loads the columns of the configured report. A no-op while a request is in
  // flight or once the columns are in hand: re-opening the drawer keeps
  // in-progress edits, and a second click can't race two responses into the
  // same state. Calling it from the error state retries.
  async function load(): Promise<void> {
    if (!reportId.value) return
    if (status.value === 'loading' || status.value === 'loaded') return

    status.value = 'loading'

    try {
      const response = await fetchColumnsRequest({
        reportId: reportId.value,
        reportPeriodSetId: reportPeriodSetId.value,
      })

      // Each column gets a stable client-side uid so edits don't lose track of
      // rows (the server id is null for columns added here).
      columns.value = response.columns.map((column) => ({
        uid: nextUid(),
        id: column.id,
        type: column.type,
        values: { ...column.values },
        displaced: {},
      }))
      schema.value = response.schema
      defaults.value = response.defaults
      status.value = 'loaded'
    } catch (error) {
      SfLog.error("Failed to load the report's columns", {
        error,
        extra: { reportId: reportId.value },
      })
      status.value = 'error'
    }
  }

  function open(): void {
    isOpen.value = true
  }

  function close(): void {
    isOpen.value = false
  }

  // Only the types the "+" buttons offer: `defaults` carries starting values for
  // those three alone, so adding any other type would push a column with no
  // values and expand into a form of empty selects.
  function addColumn(type: AddableType): void {
    columns.value.push({
      uid: nextUid(),
      id: null,
      type,
      values: { ...(defaults.value[type] ?? {}) },
      displaced: {},
    })
  }

  function removeColumn(uid: string): void {
    const index = columns.value.findIndex((column) => column.uid === uid)
    if (index !== -1) columns.value.splice(index, 1)
  }

  // The list order *is* the column order, so a drag has nothing to keep in step
  // beyond the array vuedraggable hands back (the same rows, resequenced).
  function reorderColumns(reordered: EditorColumn[]): void {
    columns.value = reordered
  }

  function updateColumnValue(uid: string, key: string, value: string): void {
    const column = columns.value.find((candidate) => candidate.uid === uid)
    if (!column) return

    // The user picking a value for a field settles that field: whatever a rule
    // had displaced there is no longer theirs to get back.
    delete column.displaced[key]
    column.values[key] = value

    // Some values rule others out, and the rest of the column has to follow:
    // choosing actuals plus budget makes it a whole-year column, so a period or
    // year saying otherwise moves to one that agrees — and moves back when the
    // rule lifts. Applied here rather than in the form so the column is never
    // briefly in a state it can't be saved in — everything else reading the
    // store sees a column that already agrees with itself.
    Object.assign(column, settleColumn(column, schema.value[column.type], key))
  }

  return {
    isOpen,
    status,
    reportId,
    columns,
    schema,
    defaults,
    configure,
    load,
    open,
    close,
    addColumn,
    removeColumn,
    reorderColumns,
    updateColumnValue,
  }
})
